// Powered by AI strip, used on the homepage to highlight the AI-native nature of the suite.
import React from "react";
import { Link } from "react-router-dom";
import { Sparkles, Brain, Wand2, Scan, GitBranch, Bot, ArrowRight } from "lucide-react";

const AI_PILLARS = [
  {
    Icon: Brain,
    t: "AI compliance copilot",
    d: "Drafts board minutes, summarises filings and surfaces deadlines before they bite, trained on UK Companies House rules.",
  },
  {
    Icon: Scan,
    t: "Document understanding",
    d: "Auto-extracts shareholder, PSC and officer data from PDFs, certificates and onboarding packs. No more re-keying.",
  },
  {
    Icon: Wand2,
    t: "AI-assisted forms",
    d: "Tell CoForm what you want to file in plain English. We turn it into a fully validated Companies House submission.",
  },
  {
    Icon: GitBranch,
    t: "Group-structure intelligence",
    d: "Build a map of any UK group in seconds. CoSearch reasons across 5m+ companies, directors and PSCs.",
  },
  {
    Icon: Bot,
    t: "ID verification with AI",
    d: "CoVerify combines biometric liveness, document AI and AML screening. ECCTA-ready, all under 60 seconds.",
  },
];

export default function PoweredByAI() {
  return (
    <section
      id="ai"
      className="relative overflow-hidden bg-primary text-primary-foreground"
      data-testid="home-ai-section"
    >
      <div className="absolute inset-0 bg-dotgrid opacity-[0.08] pointer-events-none" />
      <div className="absolute -top-32 -right-32 h-[28rem] w-[28rem] rounded-full bg-accent/12 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 h-[24rem] w-[24rem] rounded-full bg-accent/8 blur-3xl pointer-events-none" />

      <div className="container-cv relative py-20 md:py-28">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-accent">
              <Sparkles className="h-3.5 w-3.5" /> Powered by AI
            </span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl font-bold tracking-tight leading-[1.05]">
              Built AI-native, not AI-bolted-on.{" "}
              <span className="text-accent">The whole suite uses it.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-base md:text-lg text-white/75 leading-relaxed">
              CoSec Global was designed from day one to use AI across every product, drafting, extraction,
              search, identity verification and compliance reasoning. Not a wrapper. Not a chatbot. The real
              productivity layer behind every filing, board pack and KYC check.
            </p>
            <Link
              to="/ai"
              data-testid="home-ai-learn-more"
              className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-accent hover:text-white transition-colors"
            >
              See how we use AI <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {AI_PILLARS.map((p) => (
            <div
              key={p.t}
              className="group relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-sm p-6 hover:-translate-y-1 hover:border-accent/50 hover:bg-white/[0.06] transition-all"
            >
              <div className="h-11 w-11 grid place-items-center rounded-xl bg-accent/15 text-accent">
                <p.Icon className="h-5 w-5" />
              </div>
              <div className="mt-4 font-display text-lg font-bold tracking-tight">{p.t}</div>
              <p className="mt-2 text-sm text-white/70 leading-relaxed">{p.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
